import { ArrowLeft } from "lucide-react";
import { Doc, Id } from "@cvx/_generated/dataModel";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { api } from "@cvx/_generated/api";
import { useQuery } from "convex/react";

// Import extracted components
import { AnalyticsCharts } from "@/components/SuperAdminDashboard/AnalyticsCharts";
import { DateRangeFilter } from "@/components/SuperAdminDashboard/DateRangeFilter";
import {
  Filters,
  type FilterMode,
} from "@/components/SuperAdminDashboard/Filters";
import { Leaderboard } from "@/components/SuperAdminDashboard/Leaderboard";
import { StatsCards } from "@/components/SuperAdminDashboard/StatsCards";
import { TopVideos } from "@/components/SuperAdminDashboard/TopVideos";
import { VideoProjectStatsTable } from "@/components/SuperAdminDashboard/VideoProjectStatsTable";
import { VideosTable } from "@/components/SuperAdminDashboard/VideosTable";
import {
  calculateCost,
  type Video,
} from "@/components/SuperAdminDashboard/calculateCost";
import {
  useChartData,
  type PlatformData,
} from "@/components/SuperAdminDashboard/useChartData";

type DashboardVideo = Doc<"contractVideos"> & {
  views?: number;
  likes?: number;
  comments?: number;
  shares?: number;
  bookmarks?: number;
  earnings?: number;
};

type CampaignWithApp = Doc<"campaigns"> & {
  app: { _id: Id<"apps">; name: string } | null;
};

type CreatorWithNameFromQuery = {
  _id: Id<"creators">;
  name: string;
  userId: Id<"users">;
};

type CreatorWithNameFromDashboard = Doc<"creators"> & {
  name: string;
};

export const Route = createFileRoute("/superadmin-dashboard")({
  component: SuperAdminDashboard,
});


function SuperAdminDashboard() {
  const [filterMode, setFilterMode] = useState<FilterMode>("campaigns");
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<
    Set<Id<"campaigns">>
  >(new Set());
  const [selectedCreatorIds, setSelectedCreatorIds] = useState<
    Set<Id<"creators">>
  >(new Set());
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [creatorSearchQuery, setCreatorSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });
  const [showAllCampaigns, setShowAllCampaigns] = useState(false);
  const [showAllCreators, setShowAllCreators] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);

  // Refs to track if we've initialized default selections (prevent duplicate initializations)
  const hasInitializedCampaignRef = useRef(false);
  const hasInitializedCreatorRef = useRef(false);
  const hasInitializedTagRef = useRef(false);

  // Check if user is authenticated and is a team member
  const user = useQuery(api.app.getCurrentUser, {});

  const isTeamMember = user ? TEAM_USER_IDS.includes(user._id) : false;

  // Get all campaigns for team members
  const allCampaigns = useQuery(
    api.campaigns.getAllCampaignsForTeam,
    isTeamMember ? {} : "skip",
  );

  // Get creators for search (only when in creators mode)
  const allCreators = useQuery(
    api.creators.getAllCreatorsForTeam,
    isTeamMember && filterMode === "creators"
      ? { searchQuery: creatorSearchQuery || undefined }
      : "skip",
  );

  // Available tags
  const availableTags = ["UGC Accelerator", "Creator Partner", "CiR"];

  // Set first campaign as default when campaigns load (only once)
  useEffect(() => {
    if (
      filterMode === "campaigns" &&
      allCampaigns &&
      allCampaigns.length > 0 &&
      selectedCampaignIds.size === 0 &&
      !showAllCampaigns &&
      !hasInitializedCampaignRef.current
    ) {
      hasInitializedCampaignRef.current = true;
      setSelectedCampaignIds(new Set([allCampaigns[0]._id]));
    }
    // Reset ref when filter mode changes away from campaigns
    if (filterMode !== "campaigns") {
      hasInitializedCampaignRef.current = false;
    }
  }, [filterMode, allCampaigns, selectedCampaignIds.size, showAllCampaigns]);

  // Set first creator as default when creators load and filter mode is creators (only once)
  useEffect(() => {
    if (
      filterMode === "creators" &&
      allCreators &&
      allCreators.length > 0 &&
      selectedCreatorIds.size === 0 &&
      !hasInitializedCreatorRef.current
    ) {
      hasInitializedCreatorRef.current = true;
      setSelectedCreatorIds(new Set([allCreators[0]._id]));
    }
    // Reset ref when filter mode changes away from creators
    if (filterMode !== "creators") {
      hasInitializedCreatorRef.current = false;
    }
  }, [filterMode, allCreators, selectedCreatorIds.size]);

  // Set first tag as default when filter mode is tags (only once)
  useEffect(() => {
    if (
      filterMode === "tags" &&
      availableTags.length > 0 &&
      selectedTags.size === 0 &&
      !hasInitializedTagRef.current
    ) {
      hasInitializedTagRef.current = true;
      setSelectedTags(new Set([availableTags[0]]));
    }
    // Reset ref when filter mode changes away from tags
    if (filterMode !== "tags") {
      hasInitializedTagRef.current = false;
    }
  }, [filterMode, availableTags, selectedTags.size]);

  // Convert Sets to arrays for queries
  const campaignIdsArray = useMemo(() => {
    if (showAllCampaigns) return [];
    return Array.from(selectedCampaignIds).sort((a, b) => a.localeCompare(b));
  }, [showAllCampaigns, selectedCampaignIds]);

  const creatorIdsArray = useMemo(() => {
    if (showAllCreators) return [];
    return Array.from(selectedCreatorIds).sort((a, b) => a.localeCompare(b));
  }, [showAllCreators, selectedCreatorIds]);

  const tagsArray = useMemo(() => {
    if (showAllTags) return [];
    return Array.from(selectedTags).sort();
  }, [showAllTags, selectedTags]);

  // Prepare dateRange for queries (convert to timestamps if provided)
  const dateRangeForQuery = useMemo(() => {
    if (dateRange.startDate && dateRange.endDate) {
      return {
        startDate: new Date(dateRange.startDate).getTime(),
        endDate: new Date(dateRange.endDate + "T23:59:59").getTime(),
      };
    }
    return undefined;
  }, [dateRange.startDate, dateRange.endDate]);

  // Handler to add a campaign
  const handleAddCampaign = (campaignId: Id<"campaigns">) => {
    setSelectedCampaignIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(campaignId);
      return newSet;
    });
    setIsPopoverOpen(false);
  };

  // Handler to remove a campaign
  const handleRemoveCampaign = (campaignId: Id<"campaigns">) => {
    setSelectedCampaignIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(campaignId);
      return newSet;
    });
  };

  // Handler to add a creator
  const handleAddCreator = (creatorId: Id<"creators">) => {
    setSelectedCreatorIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(creatorId);
      return newSet;
    });
    setCreatorSearchQuery("");
  };

  // Handler to remove a creator
  const handleRemoveCreator = (creatorId: Id<"creators">) => {
    setSelectedCreatorIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(creatorId);
      return newSet;
    });
  };

  // Handler to toggle a tag
  const handleToggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tag)) {
        newSet.delete(tag);
      } else {
        newSet.add(tag);
      }
      return newSet;
    });
  };

  // Get campaigns that are not yet selected
  const availableCampaigns =
    allCampaigns?.filter(
      (campaign) => !selectedCampaignIds.has(campaign._id),
    ) ?? [];

  // Get creators that are not yet selected
  const availableCreators =
    allCreators?.filter((creator) => !selectedCreatorIds.has(creator._id)) ??
    [];

  // Get selected creators for display
  const selectedCreators: CreatorWithNameFromQuery[] =
    allCreators?.filter((creator) => selectedCreatorIds.has(creator._id)) ?? [];

  // Conditional data queries based on filter mode
  const campaignData = useQuery(
    api.dashboard.getApprovedVideosForCampaigns,
    filterMode === "campaigns" &&
      (showAllCampaigns || campaignIdsArray.length > 0) &&
      isTeamMember
      ? {
        campaignIds: campaignIdsArray,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  const creatorData = useQuery(
    api.dashboard.getApprovedVideosForCreators,
    filterMode === "creators" &&
      (showAllCreators || creatorIdsArray.length > 0) &&
      isTeamMember
      ? {
        creatorIds: creatorIdsArray,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  const tagData = useQuery(
    api.dashboard.getApprovedVideosForTags,
    filterMode === "tags" &&
      (showAllTags || tagsArray.length > 0) &&
      isTeamMember
      ? {
        tags: tagsArray,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  // Use the appropriate data based on filter mode
  const data =
    filterMode === "campaigns"
      ? campaignData
      : filterMode === "creators"
        ? creatorData
        : tagData;

  // Progressive loading: only load analytics after main data has loaded
  const shouldLoadAnalytics = data !== undefined && !!data;

  // Conditional analytics queries based on filter mode
  const campaignAnalytics = useQuery(
    api.analytics.getMultipleCampaignsVideoAnalytics,
    shouldLoadAnalytics &&
      filterMode === "campaigns" &&
      (showAllCampaigns || campaignIdsArray.length > 0) &&
      isTeamMember
      ? {
        campaignIds: campaignIdsArray,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  const creatorAnalytics = useQuery(
    api.analytics.getCreatorsVideoAnalytics,
    shouldLoadAnalytics &&
      filterMode === "creators" &&
      (showAllCreators || creatorIdsArray.length > 0) &&
      isTeamMember
      ? {
        creatorIds: creatorIdsArray,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  const tagAnalytics = useQuery(
    api.analytics.getTagsVideoAnalytics,
    shouldLoadAnalytics &&
      filterMode === "tags" &&
      (showAllTags || tagsArray.length > 0) &&
      isTeamMember
      ? {
        tags: tagsArray,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  // Use the appropriate analytics data
  const analyticsData =
    filterMode === "campaigns"
      ? campaignAnalytics
      : filterMode === "creators"
        ? creatorAnalytics
        : tagAnalytics;

  // Conditional leaderboard queries
  const campaignLeaderboard = useQuery(
    api.analytics.getMultipleCampaignsLeaderboard,
    shouldLoadAnalytics &&
      filterMode === "campaigns" &&
      (showAllCampaigns || campaignIdsArray.length > 0) &&
      isTeamMember
      ? {
        campaignIds: campaignIdsArray,
        limit: 20,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  const creatorLeaderboard = useQuery(
    api.analytics.getCreatorsLeaderboard,
    shouldLoadAnalytics &&
      filterMode === "creators" &&
      (showAllCreators || creatorIdsArray.length > 0) &&
      isTeamMember
      ? {
        creatorIds: creatorIdsArray,
        limit: 20,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  const tagLeaderboard = useQuery(
    api.analytics.getTagsLeaderboard,
    shouldLoadAnalytics &&
      filterMode === "tags" &&
      (showAllTags || tagsArray.length > 0) &&
      isTeamMember
      ? {
        tags: tagsArray,
        limit: 20,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  const leaderboardData =
    filterMode === "campaigns"
      ? campaignLeaderboard
      : filterMode === "creators"
        ? creatorLeaderboard
        : tagLeaderboard;

  // Conditional top videos queries
  const campaignTopVideos = useQuery(
    api.analytics.getMultipleCampaignsTopVideos,
    shouldLoadAnalytics &&
      filterMode === "campaigns" &&
      (showAllCampaigns || campaignIdsArray.length > 0) &&
      isTeamMember
      ? {
        campaignIds: campaignIdsArray,
        limit: 5,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  const creatorTopVideos = useQuery(
    api.analytics.getCreatorsTopVideos,
    shouldLoadAnalytics &&
      filterMode === "creators" &&
      (showAllCreators || creatorIdsArray.length > 0) &&
      isTeamMember
      ? {
        creatorIds: creatorIdsArray,
        limit: 5,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  const tagTopVideos = useQuery(
    api.analytics.getTagsTopVideos,
    shouldLoadAnalytics &&
      filterMode === "tags" &&
      (showAllTags || tagsArray.length > 0) &&
      isTeamMember
      ? {
        tags: tagsArray,
        limit: 5,
        dateRange: dateRangeForQuery,
      }
      : "skip",
  );

  const topVideosData =
    filterMode === "campaigns"
      ? campaignTopVideos
      : filterMode === "creators"
        ? creatorTopVideos
        : tagTopVideos;

  // Video project stats queries
  const campaignVideoProjectStats = useQuery(
    api.dashboard.getCampaignsVideoProjectStats,
    shouldLoadAnalytics &&
      filterMode === "campaigns" &&
      (showAllCampaigns || campaignIdsArray.length > 0) &&
      isTeamMember
      ? {
        campaignIds: campaignIdsArray,
      }
      : "skip",
  );

  const creatorVideoProjectStats = useQuery(
    api.dashboard.getCreatorVideoProjectStats,
    shouldLoadAnalytics &&
      filterMode === "creators" &&
      creatorIdsArray.length > 0 &&
      isTeamMember
      ? {
        creatorIds: creatorIdsArray,
      }
      : "skip",
  );

  const tagsVideoProjectStats = useQuery(
    api.dashboard.getTagsVideoProjectStats,
    shouldLoadAnalytics &&
      filterMode === "tags" &&
      tagsArray.length > 0 &&
      isTeamMember
      ? {
        tags: tagsArray,
      }
      : "skip",
  );

  const videoProjectStatsData =
    filterMode === "campaigns"
      ? campaignVideoProjectStats
      : filterMode === "creators"
        ? creatorVideoProjectStats
        : tagsVideoProjectStats;

  const dashboardTitle = useMemo(() => {
    return "Super Admin Dashboard";
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined" && dashboardTitle) {
      document.title = dashboardTitle;
    }
  }, [dashboardTitle]);

  // Process data for charts using extracted hook
  const chartData = useChartData(
    analyticsData as Parameters<typeof useChartData>[0],
  );

  const hasVideoAnalytics =
    ((analyticsData as { platforms?: unknown[] })?.platforms?.length ?? 0) > 0;

  // Check if we have any selections based on filter mode
  const hasSelections =
    filterMode === "campaigns"
      ? showAllCampaigns || campaignIdsArray.length > 0
      : filterMode === "creators"
        ? showAllCreators || creatorIdsArray.length > 0
        : showAllTags || tagsArray.length > 0;

  // Early returns after all hooks
  if (user === undefined) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-primary/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !isTeamMember) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center gap-4">
          <p className="text-lg text-primary/60">Access Denied</p>
          <p className="text-sm text-primary/60">
            You must be a team member to view this dashboard.
          </p>
        </div>
      </div>
    );
  }

  // Show loading state only if we're actually loading and have selections
  // If we don't have selections yet, show the selection message instead
  if ((data === undefined || allCampaigns === undefined) && hasSelections) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-primary/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Show message if no campaigns/creators/tags available
  if (
    filterMode === "campaigns" &&
    allCampaigns !== undefined &&
    allCampaigns.length === 0
  ) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center gap-4">
          <p className="text-lg text-primary/60">No campaigns found</p>
          <p className="text-sm text-primary/60">
            There are no campaigns available to view.
          </p>
        </div>
      </div>
    );
  }

  if (!data || !hasSelections) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col items-center justify-center gap-4">
          <p className="text-lg text-primary/60">
            {filterMode === "campaigns"
              ? "No campaigns selected"
              : filterMode === "creators"
                ? "No creators selected"
                : "No tags selected"}
          </p>
          <p className="text-sm text-primary/60">
            {filterMode === "campaigns"
              ? "Please select at least one campaign to view the dashboard."
              : filterMode === "creators"
                ? "Please select at least one creator to view the dashboard."
                : "Please select at least one tag to view the dashboard."}
          </p>
        </div>
      </div>
    );
  }

  // Now we can safely use data since we've checked it above
  // Type assertion: all three query types return the same structure
  const typedData = data as {
    videos: DashboardVideo[];
    creators: CreatorWithNameFromDashboard[];
    campaigns: Doc<"campaigns">[];
    contracts: Doc<"creatorContracts">[];
    videoToContractMap: Record<string, string>;
    contractToCreatorMap: Record<string, string>;
  };
  const videos = typedData.videos;
  const creators = typedData.creators;
  const campaigns = typedData.campaigns;
  const contracts = typedData.contracts;
  const videoToContractMap = typedData.videoToContractMap;
  const contractToCreatorMap = typedData.contractToCreatorMap;

  // Calculate stats
  const totalViews = videos.reduce((sum, video) => sum + (video.views ?? 0), 0);
  const totalVideos = videos.length;

  // Calculate unique creators count
  const uniqueCreatorIds = new Set<string>();
  for (const video of videos) {
    const contractId = videoToContractMap[video._id];
    if (contractId) {
      const creatorId = contractToCreatorMap[contractId];
      if (creatorId) {
        uniqueCreatorIds.add(creatorId);
      }
    }
  }
  const uniqueCreatorsCount = uniqueCreatorIds.size;

  // Calculate cost using extracted utility
  const costResult = calculateCost(
    videos as Video[],
    contracts,
    campaigns,
    videoToContractMap,
  );

  // Create a map of creator info for display
  const creatorMap = new Map<string, CreatorWithNameFromDashboard>(
    creators.map((c: CreatorWithNameFromDashboard) => [c._id as string, c]),
  );

  // Get selected campaigns for display
  const selectedCampaigns: CampaignWithApp[] =
    allCampaigns?.filter((c) => selectedCampaignIds.has(c._id)) ?? [];

  return (
    <TooltipProvider>
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="flex items-center justify-center w-10 h-10 rounded-lg border border-border bg-card hover:bg-secondary transition-colors"
                aria-label="Back to dashboard"
              >
                <ArrowLeft className="h-5 w-5 text-primary" />
              </Link>
              <h1 className="text-3xl font-bold text-primary">
                {dashboardTitle}
              </h1>
            </div>

            {/* Time Filter */}
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onStartDateChange={(date) =>
                setDateRange((prev) => ({ ...prev, startDate: date }))
              }
              onEndDateChange={(date) =>
                setDateRange((prev) => ({ ...prev, endDate: date }))
              }
              onClear={() => setDateRange({ startDate: "", endDate: "" })}
            />

            {/* Filter Mode Selector */}
            <Filters
              filterMode={filterMode}
              onFilterModeChange={setFilterMode}
              showAllCampaigns={showAllCampaigns}
              onShowAllCampaignsChange={(show) => {
                setShowAllCampaigns(show);
                if (show) {
                  setSelectedCampaignIds(new Set());
                }
              }}
              selectedCampaigns={selectedCampaigns}
              availableCampaigns={availableCampaigns}
              onAddCampaign={handleAddCampaign}
              onRemoveCampaign={handleRemoveCampaign}
              isPopoverOpen={isPopoverOpen}
              onPopoverOpenChange={setIsPopoverOpen}
              showAllCreators={showAllCreators}
              onShowAllCreatorsChange={(show) => {
                setShowAllCreators(show);
                if (show) {
                  setSelectedCreatorIds(new Set());
                }
              }}
              selectedCreators={selectedCreators}
              availableCreators={availableCreators}
              creatorSearchQuery={creatorSearchQuery}
              onCreatorSearchQueryChange={setCreatorSearchQuery}
              onAddCreator={handleAddCreator}
              onRemoveCreator={handleRemoveCreator}
              isCreatorsLoading={allCreators === undefined}
              showAllTags={showAllTags}
              onShowAllTagsChange={(show) => {
                setShowAllTags(show);
                if (show) {
                  setSelectedTags(new Set());
                }
              }}
              selectedTags={selectedTags}
              availableTags={availableTags}
              onToggleTag={handleToggleTag}
            />
          </div>

          {/* Stats Section */}
          <StatsCards
            totalViews={totalViews}
            totalVideos={totalVideos}
            uniqueCreatorsCount={uniqueCreatorsCount}
            cost={costResult.cost}
            predefinedCpm={costResult.predefinedCpm}
            hasChallengeCampaigns={costResult.hasChallengeCampaigns}
            hasNonChallengeCampaigns={costResult.hasNonChallengeCampaigns}
            costBreakdown={costResult.costBreakdown}
          />

          {/* Video Analytics Charts */}
          <AnalyticsCharts
            isLoading={analyticsData === undefined}
            hasVideoAnalytics={hasVideoAnalytics}
            chartData={chartData}
            platforms={
              (analyticsData as { platforms?: PlatformData[] })?.platforms as
              | PlatformData[]
              | undefined
            }
          />

          {/* Top Videos */}
          <TopVideos
            isLoading={topVideosData === undefined}
            videos={
              (
                topVideosData as {
                  videos?: Array<{
                    videoId: string;
                    rank: number;
                    views: number;
                    medianMultiplier: number;
                    title?: string;
                    url: string;
                    platform: string;
                    creator: { name: string; followers: number };
                  }>;
                }
              )?.videos as
              | Array<{
                videoId: string;
                rank: number;
                views: number;
                medianMultiplier: number;
                title?: string;
                url: string;
                platform: string;
                creator: { name: string; followers: number };
              }>
              | undefined
            }
          />

          {/* Leaderboard */}
          <Leaderboard
            isLoading={leaderboardData === undefined}
            creators={
              leaderboardData as Parameters<typeof Leaderboard>[0]["creators"]
            }
          />

          {/* Video Project Stats Table */}
          {(filterMode === "campaigns" ||
            filterMode === "creators" ||
            filterMode === "tags") && (
              <VideoProjectStatsTable
                isLoading={videoProjectStatsData === undefined}
                stats={
                  videoProjectStatsData as Parameters<
                    typeof VideoProjectStatsTable
                  >[0]["stats"]
                }
              />
            )}

          {/* Videos Table */}
          <VideosTable
            videos={videos}
            videoToContractMap={videoToContractMap}
            contractToCreatorMap={contractToCreatorMap}
            creatorMap={creatorMap}
          />
        </div>
      </div>
    </TooltipProvider>
  );
}
